<template>
  <!-- One Launchpad section: header with progress, then the checklist and learn columns -->
  <section class="mt-6 bg-white border border-border-normal rounded-2xl overflow-hidden">
    <!-- Section header -->
    <div class="px-6 py-5 border-b border-border-normal flex flex-col sm:flex-row sm:items-center gap-4">
      <div class="flex-grow min-w-0">
        <BaseText
          type="heading"
          size="overline"
          class="text-text-subdued uppercase"
        >
          {{ section.kicker }}
        </BaseText>
        <BaseText
          type="heading"
          size="lg"
          class="text-text-loud mt-1.5"
        >
          {{ section.title }}
        </BaseText>
        <BaseText
          type="body"
          size="sm"
          class="text-text-normal mt-1.5 max-w-3xl"
        >
          {{ section.blurb }}
        </BaseText>

        <!-- Which tools this section is about -->
        <SetupToolsBar
          v-if="section.tools && section.tools.length"
          class="mt-4"
          :tools="section.tools"
          :section-id="section.id"
          :title="toolsTitle"
          :phone="phone"
        />
      </div>
      <div
        v-if="progress.total"
        class="flex-shrink-0 flex sm:flex-col items-center sm:items-end gap-2"
      >
        <BaseText
          type="label"
          size="xs"
          class="text-text-subdued whitespace-nowrap"
        >
          {{ progress.done }} / {{ progress.total }}
        </BaseText>
        <div class="w-28 h-1.5 rounded-full overflow-hidden bg-neutral-50">
          <div
            class="h-full rounded-full transition-all duration-300 ease-in-out"
            :class="accentBarClass"
            :style="{ width: `${barPercent}%` }"
          />
        </div>
      </div>
    </div>

    <!-- Checklist + learn columns -->
    <div
      class="grid grid-cols-1"
      :class="hasLearnContent ? 'lg:grid-cols-5' : ''"
    >
      <div
        class="p-2"
        :class="checklistColumnClass"
      >
        <!-- Phone: read-only rows -->
        <template v-if="phone">
          <SetupPhoneTaskRow
            v-for="step in steps"
            :key="step.id"
            :step="step"
            :pill-label="phonePillLabel"
          />
        </template>
        <template v-else>
          <SetupTaskRow
            v-for="step in steps"
            :key="step.id"
            :step="step"
            :skipping="skippingStepId === step.id"
            @cta="$emit('cta', $event)"
            @skip="$emit('skip', $event)"
          />
        </template>
      </div>
      <SetupLearnPanel
        v-if="hasLearnContent"
        class="lg:col-span-2"
        :class="phone ? 'order-first border-b border-border-normal' : ''"
        :learn="section.learn"
        :section-id="section.id"
      />
    </div>

    <!-- MCP: prompts and skill packs span the full width under the columns -->
    <SetupMcpPanel
      v-if="section.id === 'mcp'"
      class="border-t border-border-normal"
      :mcp-connected="mcpConnected"
      :phone="phone"
    />
  </section>
</template>

<script>
import SetupTaskRow from './SetupTaskRow.vue'
import SetupPhoneTaskRow from './SetupPhoneTaskRow.vue'
import SetupToolsBar from './SetupToolsBar.vue'
import SetupLearnPanel from './SetupLearnPanel.vue'
import SetupMcpPanel from './SetupMcpPanel.vue'
import { SETUP_TOOLS_TITLE, SETUP_PHONE } from '@/utils/setup/setupContent'
import { hasVideoMedia } from '@/utils/setup/setupVideo'

// Literal class strings so Tailwind's purge keeps them
const ACCENT_BAR_CLASSES = {
  blue: 'bg-primary-blue-100',
  yellow: 'bg-secondary-yellow-100'
}

export default {
  name: 'SetupSection',
  components: { SetupTaskRow, SetupPhoneTaskRow, SetupToolsBar, SetupLearnPanel, SetupMcpPanel },
  props: {
    // { id, accent, kicker, title, blurb, tools?, learn? }
    section: {
      type: Object,
      required: true
    },
    steps: {
      type: Array,
      required: true
    },
    // { done, total, complete }, counts only steps the user has access to
    progress: {
      type: Object,
      default: () => ({ done: 0, total: 0, complete: false })
    },
    // Phone version: read-only rows, videos above the checklist
    phone: {
      type: Boolean,
      default: false
    },
    // Step whose skip / undo request is in flight
    skippingStepId: {
      type: String,
      default: null
    }
  },
  data () {
    return { toolsTitle: SETUP_TOOLS_TITLE, phonePillLabel: SETUP_PHONE.pillLabel }
  },
  computed: {
    // A learn column with no playable video collapses so the checklist takes the full width
    hasLearnContent () {
      const { learn } = this.section
      if (!learn) return false
      return hasVideoMedia(learn.video) || (learn.secondaryVideos || []).some(hasVideoMedia)
    },
    // Phones show the videos above the checklist, so the divider moves to the
    // learn panel
    checklistColumnClass () {
      if (!this.hasLearnContent) return ''
      if (this.phone) return 'lg:col-span-3 lg:border-r border-border-normal'
      return 'lg:col-span-3 border-b lg:border-b-0 lg:border-r border-border-normal'
    },
    accentBarClass () {
      return ACCENT_BAR_CLASSES[this.section.accent] || ACCENT_BAR_CLASSES.blue
    },
    barPercent () {
      return this.progress.total ? (this.progress.done / this.progress.total) * 100 : 0
    },
    // Hides the MCP panel's "connect first" note; a skipped connect step still shows it
    mcpConnected () {
      return this.steps.some((step) => step.id === 'mcp_connected' && step.done)
    }
  }
}
</script>
